import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { detectPlatform, getDeferredPrompt, triggerInstall } from "@/lib/install";

const DISMISS_KEY = "vismarks_install_dismissed_at";
const DAYS = 7;

export default function InstallPrompt() {
  const [ready, setReady] = useState(!!getDeferredPrompt());
  const [show, setShow] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    if (p.standalone || localStorage.getItem("vismarks_app_installed") === "1") return;
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - at < DAYS * 864e5) return;
    if (window.location.pathname === "/app") return;
    const on = () => setReady(true);
    const done = () => setShow(false);
    window.addEventListener("vsk-install-ready", on);
    window.addEventListener("vsk-install-done", done);
    const t = setTimeout(() => setShow(true), 4000);
    return () => { window.removeEventListener("vsk-install-ready", on); window.removeEventListener("vsk-install-done", done); clearTimeout(t); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    const outcome = await triggerInstall();
    if (outcome === "accepted") { toast.success("VISMARKS is installing — check your home screen"); setShow(false); }
    else if (outcome === "dismissed") dismiss();
    else window.location.assign("/app");
  };

  if (!show) return null;
  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 right-4 sm:left-auto sm:w-96 z-50 rounded-xl border border-[#27272A] bg-[#121212]/95 backdrop-blur-md p-4 shadow-2xl shadow-black/60" data-testid="install-prompt">
      <div className="flex items-start gap-3">
        <Link to="/app" onClick={() => setShow(false)}><img src="/icon-192.png" alt="VISMARKS app" className="h-10 w-10 rounded-lg border border-[#27272A] shrink-0" /></Link>
        <div className="flex-1 min-w-0">
          <Link to="/app" onClick={() => setShow(false)} className="font-black text-sm text-white tracking-tight hover:underline" data-testid="install-prompt-title">Get the VISMARKS app</Link>
          <p className="text-xs text-neutral-400 mt-1">{ready ? "One tap — opens full-screen from your home screen, with enquiry and sale alerts." : "Ten seconds from your browser, no app store needed."}</p>
          <div className="mt-2.5 flex items-center gap-2">
            {ready ? (
              <button onClick={install} data-testid="install-app-btn" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#FF3B30] text-white text-xs font-bold hover:bg-[#e0342a] transition-colors"><Download className="h-3.5 w-3.5" /> Install app</button>
            ) : (
              <Link to="/app" onClick={() => setShow(false)} data-testid="install-howto-btn" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-black text-xs font-bold hover:bg-neutral-200 transition-colors">How to install <ArrowRight className="h-3.5 w-3.5" /></Link>
            )}
          </div>
        </div>
        <button onClick={dismiss} data-testid="install-dismiss-btn" aria-label="Dismiss" className="text-neutral-500 hover:text-white transition-colors shrink-0"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
